import { formatAccuracyFeet } from './unitConversion';

/**
 * Elevation and reference-frame disclosure shared by the human-facing geo-item surfaces (pothole
 * PDF, KMZ, CSV, web form) so they cannot drift apart on how a height is named or rendered.
 *
 * **`geometry.coordinates[2]` is not elevation.** Z is the ellipsoidal height in
 * `accuracy.referenceFrame` per RFC 7946 §4 — roughly 28 m from height above sea level. Orthometric
 * height lives in `accuracy.mslHeight`, corrected at capture. Neither is derived from the other:
 * `Z − undulation` is not NAVD88, since the two are referenced to frames ~1.2 m apart.
 */

/**
 * Label for the orthometric height. Datum-qualified because `Elevation` on its own is already a
 * user-typed Survey Results pothole prop (`potholeProps.ts`) that reaches the KMZ and the CSV.
 */
export const ELEVATION_LABEL = 'Elevation (NAVD88)';

/** Lowercase `h`, matching mobile's `ELLIPSOIDAL_HEIGHT_LABEL`. Provenance, never elevation. */
export const ELLIPSOIDAL_HEIGHT_LABEL = 'Ellipsoidal height';

/**
 * Rendered when there is no `mslHeight`. Export-only: mobile can drop the row, an export row always
 * has to say something, and `0.0 ft` would read as a pothole at sea level.
 */
export const ELEVATION_NOT_AVAILABLE = 'Not available';

/** Rendered for a frame we cannot state: no stamp at all, or the `unknown` sentinel. */
export const REFERENCE_FRAME_NOT_RECORDED = 'Not recorded';

/**
 * Sentinel values `accuracy.referenceFrame` carries, mirrored from mobile's
 * `positionAccuracy/resolveCurrentAccuracy.ts`. Device GNSS contracts to WGS-84; `unknown` covers an
 * OS-injected fix whose frame is unobservable, and must never be read as CRS84.
 */
export const DEVICE_GPS_REFERENCE_FRAME = 'CRS84';
export const UNKNOWN_REFERENCE_FRAME = 'unknown';

/**
 * One decimal foot (~3 cm), matching mobile. Coarser than the 3 decimals HRMS/VRMS use: those are
 * precisions, and trailing millimetres on a height imply a certainty the geoid model lacks.
 */
const ELEVATION_DECIMALS = 1;

/**
 * The provenance half of `accuracy`, mirrored from `geoItemAccuracySchema`
 * (`packages/db/src/schema/projects.ts`) because `packages/client` has no `@civilgrid/db`
 * dependency. Every field is optional; device-GPS captures write only `referenceFrame`.
 */
export interface GeoItemElevationAccuracy {
  /** Orthometric height in metres, NAVD88, corrected at capture. Null/absent means "no elevation". */
  mslHeight?: number | null;
  /**
   * The frame the coordinates are actually in, verbatim from the receiver — `'WGS84 [current]'`,
   * `'NAD83(2011) (EPOCH:2010)'`, `'ITRF2020'`, … Never parsed or matched against a list of known
   * frames; the set is open and the spellings vary by TMM version.
   */
  referenceFrame?: string | null;
  /** e.g. `'GEOID18 (Conus)'`, verbatim. Qualifies the elevation; absent means no geoid configured. */
  geoidModel?: string | null;
}

/** GeoJSON `Position` — `[lng, lat]` or `[lng, lat, ellipsoidalHeight]`. */
type PositionLike = readonly number[] | null | undefined;

/** Formats an orthometric or ellipsoidal height in metres as a feet string, with unit. */
export function formatElevationFeet(meters: number): string {
  return `${formatAccuracyFeet(meters, ELEVATION_DECIMALS)} ft`;
}

/**
 * The ellipsoidal height from a GeoJSON position, or null when the geometry carries no Z — as with
 * any 2-D pin hand-placed on the web map.
 */
export function ellipsoidalHeightFromCoordinates(coordinates: PositionLike): number | null {
  const z = coordinates?.[2];
  return typeof z === 'number' && Number.isFinite(z) ? z : null;
}

/** True when the record carries a usable orthometric height. */
function hasElevation(accuracy: GeoItemElevationAccuracy | null | undefined): boolean {
  return typeof accuracy?.mslHeight === 'number' && Number.isFinite(accuracy.mslHeight);
}

/**
 * The elevation value for a row or attribute: `115.5 ft · GEOID18 (Conus)`, or `115.5 ft` when the
 * receiver reported no geoid, or {@link ELEVATION_NOT_AVAILABLE}.
 *
 * The geoid rides in the value rather than the label because the PDF's label column is too narrow
 * for `Elevation · GEOID18 (Conus)`; mobile puts it in the label instead.
 */
export function formatElevationValue(accuracy: GeoItemElevationAccuracy | null | undefined): string {
  if (!hasElevation(accuracy)) {
    return ELEVATION_NOT_AVAILABLE;
  }
  const elevation = formatElevationFeet(accuracy?.mslHeight as number);
  return accuracy?.geoidModel ? `${elevation} · ${accuracy.geoidModel}` : elevation;
}

/** The bare elevation, no geoid qualifier — for surfaces that emit `Geoid Model` as its own field. */
export function formatElevationValueWithoutGeoid(accuracy: GeoItemElevationAccuracy | null | undefined): string {
  return hasElevation(accuracy) ? formatElevationFeet(accuracy?.mslHeight as number) : ELEVATION_NOT_AVAILABLE;
}

/**
 * The datum name for a recorded reference frame, or `null` to omit it entirely.
 *
 * `unknown` maps to null rather than a frame name so we never assert a datum we cannot back up.
 * Everything else passes through verbatim, since the set of real frame strings is open and matching
 * against a list would mislabel the next TMM spelling. To always render something, use
 * {@link formatReferenceFrameValue}.
 */
export function mapDatumForReferenceFrame(referenceFrame: string | null | undefined): string | null {
  if (!referenceFrame || referenceFrame === UNKNOWN_REFERENCE_FRAME) {
    return null;
  }
  return referenceFrame === DEVICE_GPS_REFERENCE_FRAME ? 'WGS-84' : referenceFrame;
}

/**
 * The reference frame for a row or attribute, or {@link REFERENCE_FRAME_NOT_RECORDED}. Disclosure
 * only, so a reader knows the lat/lon needs transforming before trusting it against NAD83 survey
 * control; the transform itself is FME's job.
 */
export function formatReferenceFrameValue(accuracy: GeoItemElevationAccuracy | null | undefined): string {
  return mapDatumForReferenceFrame(accuracy?.referenceFrame) ?? REFERENCE_FRAME_NOT_RECORDED;
}

/**
 * The PDF's fallback rule: show the ellipsoidal height only when there is a Z but no elevation, so a
 * reader of a page with limited room is never offered two heights 28 m apart. The KMZ and the CSV
 * always emit it — key/value attributes and fixed columns have room for both.
 */
export function shouldShowEllipsoidalHeight(
  accuracy: GeoItemElevationAccuracy | null | undefined,
  coordinates: PositionLike,
): boolean {
  return !hasElevation(accuracy) && ellipsoidalHeightFromCoordinates(coordinates) !== null;
}
